import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
    AppBar, 
    Toolbar, 
    Box, 
    Button, 
    IconButton, 
    Drawer, 
    List, 
    ListItem, 
    Container,
    useScrollTrigger,
    useMediaQuery,
    styled
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

// Custom styled components with premium animations
const StyledAppBar = styled(AppBar)(({ theme, scrolled }) => ({
    backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    borderBottom: scrolled ? '1px solid rgba(0, 102, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.08)' : 'none',
    transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)',
    animation: 'slideDown 0.5s ease-out',
    '@keyframes slideDown': {
        '0%': {
            opacity: 0,
            transform: 'translateY(-100%)',
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },
}));

const StyledToolbar = styled(Toolbar)({
    height: '84px',
    gap: '40px',
    padding: '0 24px',
    position: 'relative',
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&::before': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '0%',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, #0066FF, #6366F1, #0066FF, transparent)',
        transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: 0,
    },
    '@media (max-width: 768px)': {
        height: '70px',
        padding: '0 16px',
    },
});

const LogoContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    cursor: 'pointer',
    transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'scale(1.05)',
    },
    '&:active': {
        transform: 'scale(0.98)',
    },
});

const LogoImage = styled('img')({
    height: '72px',
    width: 'auto',
    objectFit: 'contain',
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    filter: 'drop-shadow(0 2px 4px rgba(0, 102, 255, 0.1))',
    '@media (max-width: 768px)': {
        height: '62px',
    },
    '@media (max-width: 480px)': {
        height: '56px',
    },
    '&:hover': {
        filter: 'drop-shadow(0 4px 8px rgba(0, 102, 255, 0.25))',
        transform: 'rotate(-5deg)',
    },
});

const TextLogoImage = styled('img')({
    height: 'auto',
    width: '120px',
    objectFit: 'contain',
    marginLeft: '8px',
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    filter: 'drop-shadow(0 2px 4px rgba(0, 102, 255, 0.1))',
    '@media (max-width: 968px)': {
        height: '52px',
        marginLeft: '6px',
    },
    '@media (max-width: 480px)': {
        height: '46px',
        marginLeft: '5px',
    },
    '&:hover': {
        filter: 'drop-shadow(0 4px 8px rgba(0, 102, 255, 0.25)) brightness(1.1)',
        transform: 'translateY(-1px)',
    },
});

const NavLinksContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '32px',
    flex: 1,
    justifyContent: 'center',
    animation: 'fadeIn 0.8s ease-out 0.3s backwards',
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0,
        },
        '100%': {
            opacity: 1,
        },
    },
    '@media (max-width: 968px)': {
        display: 'none',
    },
}));

const NavLink = styled('a')({
    color: '#334155',
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '14px',
    position: 'relative',
    padding: '8px 12px',
    display: 'block',
    whiteSpace: 'nowrap',
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    borderRadius: '8px',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
        borderRadius: '8px',
        opacity: 0,
        transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '4px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: '2px',
        background: 'linear-gradient(90deg, #0066FF 0%, #6366F1 100%)',
        borderRadius: '2px',
        transition: 'width 400ms cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 0 8px rgba(0, 102, 255, 0.5)',
    },
    '&:hover': {
        color: '#0066FF',
        transform: 'translateY(-2px)',
    },
    '&:hover::before': {
        opacity: 1,
    },
    '&:hover::after': {
        width: 'calc(100% - 24px)',
    },
    '&:active': {
        transform: 'translateY(0)',
    },
});

const ButtonContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    animation: 'fadeInRight 0.6s ease-out 0.5s backwards',
    '@keyframes fadeInRight': {
        '0%': {
            opacity: 0,
            transform: 'translateX(20px)',
        },
        '100%': {
            opacity: 1,
            transform: 'translateX(0)',
        },
    },
}));

const DesktopButton = styled(Button)(({ theme }) => ({
    '@media (max-width: 968px)': {
        display: 'none',
    },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
    display: 'none',
    marginLeft: '12px',
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
    border: '1px solid rgba(0, 102, 255, 0.1)',
    '&:hover': {
        background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.12) 0%, rgba(99, 102, 241, 0.12) 100%)',
        transform: 'scale(1.1) rotate(90deg)',
        boxShadow: '0 4px 12px rgba(0, 102, 255, 0.2)',
        borderColor: 'rgba(0, 102, 255, 0.3)',
    },
    '&:active': {
        transform: 'scale(0.95) rotate(90deg)',
    },
    '@media (max-width: 968px)': {
        display: 'flex',
    },
    '& .MuiSvgIcon-root': {
        transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
}));

const MobileDrawer = styled(Drawer)({
    '& .MuiDrawer-paper': {
        width: '100%',
        top: '70px',
        height: 'calc(100% - 70px)',
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 8px 32px rgba(0, 102, 255, 0.15)',
        borderTop: '2px solid rgba(0, 102, 255, 0.2)',
        animation: 'slideInFromTop 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '@keyframes slideInFromTop': {
            '0%': {
                opacity: 0,
                transform: 'translateY(-20px)',
            },
            '100%': {
                opacity: 1,
                transform: 'translateY(0)',
            },
        },
    },
});

const MobileNavList = styled(List)({
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
});

const MobileNavLink = styled('a')({
    display: 'block',
    padding: '16px 20px',
    color: '#334155',
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '16px',
    borderRadius: '12px',
    position: 'relative',
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    animation: 'fadeInUp 0.4s ease-out backwards',
    '@keyframes fadeInUp': {
        '0%': {
            opacity: 0,
            transform: 'translateY(20px)',
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: '4px',
        height: 0,
        background: 'linear-gradient(180deg, #0066FF 0%, #6366F1 100%)',
        borderRadius: '0 4px 4px 0',
        transition: 'height 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    '&:hover': {
        color: '#0066FF',
        background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
        paddingLeft: '28px',
        transform: 'translateX(8px)',
    },
    '&:hover::before': {
        height: '100%',
    },
    '&:active': {
        transform: 'translateX(4px) scale(0.98)',
    },
});

const Header = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width: 968px)');
    
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 50,
    });

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen]);

    // Close mobile menu when switching from mobile to desktop
    useEffect(() => {
        if (!isMobile && isMobileMenuOpen) {
            setIsMobileMenuOpen(false);
        }
    }, [isMobile, isMobileMenuOpen]);

    const location = useLocation();
    const isHome = location.pathname === '/';
    const isSignupFormPage = location.pathname === '/signupform';

    const handleSmoothScroll = (e, targetId) => {
        e.preventDefault();
        const target = document.querySelector(targetId);
        if (target) {
            const headerOffset = 80;
            const elementPosition = target.offsetTop;
            const offsetPosition = elementPosition - headerOffset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleMobileNavClick = (e, item) => {
        if (item.type === 'hash' && isHome) {
            handleSmoothScroll(e, item.href);
        }
        setIsMobileMenuOpen(false);
    };

    const handleRouteNavClick = (to) => {
        if (location.pathname === to) {
            window.scrollTo(0, 0);
        }
    };

    const navItems = [
        { label: 'Platform', type: 'route', to: '/platform' },
        { label: 'Pricing', type: 'route', to: '/pricing' },
        { label: 'Resources', type: 'route', to: '/resources' },
        { label: 'Company', type: 'route', to: '/company' },
        // { label: 'Contact', type: 'route', to: '/contact' },
    ];

    const homeSectionId = (href) => href.replace(/^#/, '');

    return (
        <>
            <StyledAppBar id="header" position="fixed" scrolled={trigger ? 1 : 0} elevation={0}>
                <Container maxWidth="xl">
                    <StyledToolbar disableGutters>
                        <LogoContainer component={Link} to="/" sx={{ textDecoration: 'none' }}>
                            <LogoImage src={`${process.env.PUBLIC_URL}/assets/logo.png`} alt="meDZhub Icon" />
                            <TextLogoImage src={`${process.env.PUBLIC_URL}/assets/Textlogo.png`} alt="meDZhub" />
                        </LogoContainer>

                        <NavLinksContainer>
                            {navItems.map((item) => {
                                if (item.type === 'hash') {
                                    return isHome ? (
                                        <NavLink key={item.label} href={item.href} onClick={(e) => handleSmoothScroll(e, item.href)}>
                                            {item.label}
                                        </NavLink>
                                    ) : (
                                        <Link
                                            key={item.label}
                                            to="/"
                                            state={{ homeSection: homeSectionId(item.href) }}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            <NavLink as="span" sx={{ display: 'block', cursor: 'pointer' }}>
                                                {item.label}
                                            </NavLink>
                                        </Link>
                                    );
                                }

                                return (
                                    <Link
                                        key={item.label}
                                        to={item.to}
                                        style={{ textDecoration: 'none' }}
                                        onClick={() => handleRouteNavClick(item.to)}
                                    >
                                        <NavLink as="span" sx={{ display: 'block', cursor: 'pointer' }}>
                                            {item.label}
                                        </NavLink>
                                    </Link>
                                );
                            })}
                        </NavLinksContainer>

                        <Box sx={{ 
                            flex: 1,
                            '@media (min-width: 969px)': {
                                display: 'none',
                            },
                        }} />

                        <ButtonContainer>
                            {/* {!isSignupFormPage && (
                            <DesktopButton
                                component={Link}
                                to="/"
                                state={{ homeSection: 'demo-video' }}
                                variant="outlined"
                                id="Join-button"
                                data-gtm="Join-button"
                                onClick={() => handleRouteNavClick('/')}
                                sx={{
                                    color: '#334155',
                                    borderColor: '#E2E8F0',
                                    borderWidth: '2px',
                                    padding: '13px 28px',
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    letterSpacing: '-0.2px',
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    textDecoration: 'none',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        width: '0',
                                        height: '0',
                                        borderRadius: '50%',
                                        background: 'radial-gradient(circle, rgba(0, 102, 255, 0.2) 0%, transparent 70%)',
                                        transform: 'translate(-50%, -50%)',
                                        transition: 'width 0.6s, height 0.6s',
                                    },
                                    '&:hover': {
                                        borderColor: '#0066FF',
                                        backgroundColor: 'rgba(0, 102, 255, 0.08)',
                                        color: '#0066FF',
                                        transform: 'translateY(-2px) scale(1.02)',
                                        boxShadow: '0 4px 12px rgba(0, 102, 255, 0.2)',
                                    },
                                    '&:hover::before': {
                                        width: '300px',
                                        height: '300px',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0) scale(0.98)',
                                    },
                                }}
                            >
                                See how it works
                            </DesktopButton>
                            )} */}
                            <MobileMenuButton
                                onClick={toggleMobileMenu}
                                aria-label="Toggle menu"
                            >
                                {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                            </MobileMenuButton>
                        </ButtonContainer>
                    </StyledToolbar>
                </Container>
            </StyledAppBar>

            {/* Mobile Menu Drawer */}
            <MobileDrawer
                anchor="top"
                open={isMobileMenuOpen}
                onClose={toggleMobileMenu}
                variant="persistent"
                ModalProps={{
                    keepMounted: true,
                }}
            >
                <MobileNavList>
                    {navItems.map((item, index) => (
                        <ListItem key={item.label} disablePadding>
                            {item.type === 'hash' ? (
                                isHome ? (
                                    <MobileNavLink
                                        href={item.href}
                                        onClick={(e) => handleMobileNavClick(e, item)}
                                        style={{ animationDelay: `${index * 0.08}s` }}
                                    >
                                        {item.label}
                                    </MobileNavLink>
                                ) : (
                                    <Link
                                        to="/"
                                        state={{ homeSection: homeSectionId(item.href) }}
                                        style={{ textDecoration: 'none', width: '100%' }}
                                        onClick={(e) => handleMobileNavClick(e, item)}
                                    >
                                        <MobileNavLink as="span" sx={{ display: 'block', animationDelay: `${index * 0.08}s` }}>
                                            {item.label}
                                        </MobileNavLink>
                                    </Link>
                                )
                            ) : (
                                <Link
                                    to={item.to}
                                    style={{ textDecoration: 'none', width: '100%' }}
                                    onClick={(e) => {
                                        handleRouteNavClick(item.to);
                                        handleMobileNavClick(e, item);
                                    }}
                                >
                                    <MobileNavLink as="span" sx={{ display: 'block', animationDelay: `${index * 0.08}s` }}>
                                        {item.label}
                                    </MobileNavLink>
                                </Link>
                            )}
                        </ListItem>
                    ))}
                </MobileNavList>
            </MobileDrawer>
        </>
    );
};

export default Header;

